import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const FinanceSettings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // ─── Payment-reminder settings (admin only) ───
  const [reminder, setReminder] = useState({
    enabled: true, reminder_period_days: 7, min_gap_hours: 24,
    template_name: 'payment_reminder', can_edit: false,
  });
  const [savingReminder, setSavingReminder] = useState(false);
  const fetchReminder = async () => {
    try {
      const res = await apiClient.get('/finance/reminder-settings');
      setReminder(res.data || {});
    } catch (e) { /* non-blocking */ }
  };
  useEffect(() => { fetchReminder(); }, []);
  const saveReminder = async () => {
    setSavingReminder(true);
    try {
      const res = await apiClient.put('/finance/reminder-settings', {
        enabled: reminder.enabled,
        reminder_period_days: parseInt(reminder.reminder_period_days, 10),
        min_gap_hours: parseInt(reminder.min_gap_hours, 10),
        template_name: reminder.template_name,
      });
      setReminder(prev => ({ ...prev, ...(res.data?.settings || {}) }));
      toast.success('Reminder settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSavingReminder(false); }
  };
  const runSweepNow = async () => {
    if (!window.confirm('Run reminder sweep right now? This will scan all overdue invoices and send WhatsApp reminders.')) return;
    try {
      const res = await apiClient.post('/finance/reminders/run-sweep');
      const s = res.data || {};
      toast.success(`Sweep complete · scanned ${s.scanned || 0} · sent ${s.sent || 0} · skipped ${s.skipped || 0} · failed ${s.failed || 0}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    }
  };
  const [settings, setSettings] = useState({
    // Company Details
    company_name: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_state_code: '',
    company_pincode: '',
    company_gstin: '',
    company_pan: '',
    company_email: '',
    company_phone: '',
    
    // Bank Details
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_branch: '',
    
    // UPI Details
    upi_id: '',
    
    // GST Settings
    gst_rate: 18.0,
    cgst_rate: 9.0,
    sgst_rate: 9.0,
    
    // Registration fee (pre-admission)
    registration_fee_default: 10000.0,
    proforma_prefix: 'PI',
    
    // Invoice Numbering
    invoice_prefix: 'INV',
    receipt_prefix: 'RCP',
    credit_note_prefix: 'CN',
    
    // Locked periods
    locked_periods: []
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/finance/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load finance settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.put('/finance/settings', settings);
      toast.success('Finance settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Check permissions (skip when embedded - parent handles it)
  if (!embedded && user?.role !== 'admin' && user?.role !== 'accountant' && user?.role !== 'operating_head') {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-lock text-red-600 text-2xl"></i>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-500">You do not have permission to access finance settings.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="fas fa-spinner fa-spin text-2xl text-teal-600"></i>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6"}>
      {!embedded && (
        <>
          {/* Back Button */}
          <Button variant="ghost" onClick={() => navigate('/finance')} className="h-8 text-xs sm:text-sm px-2 -mb-2">
            <i className="fas fa-arrow-left mr-1 sm:mr-2"></i> Back to Finance
          </Button>
          
          {/* Header */}
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Finance Settings</h1>
            <p className="text-xs sm:text-sm text-gray-500">Configure company GST details, bank info, and document numbering</p>
          </div>
        </>
      )}

      <Tabs defaultValue="company" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto p-1 gap-1">
          <TabsTrigger value="company" className="flex-1 min-w-[80px] text-xs sm:text-sm px-2 py-1.5">Company</TabsTrigger>
          <TabsTrigger value="bank" className="flex-1 min-w-[60px] text-xs sm:text-sm px-2 py-1.5">Bank</TabsTrigger>
          <TabsTrigger value="gst" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5">GST</TabsTrigger>
          <TabsTrigger value="numbering" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 py-1.5">Numbering</TabsTrigger>
          <TabsTrigger value="reminders" className="flex-1 min-w-[90px] text-xs sm:text-sm px-2 py-1.5">Reminders</TabsTrigger>
        </TabsList>

        {/* Company Details Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Details shown on invoices, receipts, and credit notes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Patrick's Edu Care"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GSTIN</Label>
                  <Input
                    value={settings.company_gstin}
                    onChange={(e) => handleChange('company_gstin', e.target.value.toUpperCase())}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={settings.company_address}
                  onChange={(e) => handleChange('company_address', e.target.value)}
                  placeholder="Street address, Building name"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={settings.company_city}
                    onChange={(e) => handleChange('company_city', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={settings.company_state}
                    onChange={(e) => handleChange('company_state', e.target.value)}
                    placeholder="State"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State Code</Label>
                  <Input
                    value={settings.company_state_code}
                    onChange={(e) => handleChange('company_state_code', e.target.value)}
                    placeholder="e.g., 29 for Karnataka"
                    maxLength={2}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={settings.company_pincode}
                    onChange={(e) => handleChange('company_pincode', e.target.value)}
                    placeholder="560001"
                    maxLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label>PAN Number</Label>
                  <Input
                    value={settings.company_pan}
                    onChange={(e) => handleChange('company_pan', e.target.value.toUpperCase())}
                    placeholder="AAAAA0000A"
                    maxLength={10}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={settings.company_email}
                    onChange={(e) => handleChange('company_email', e.target.value)}
                    placeholder="accounts@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={settings.company_phone}
                    onChange={(e) => handleChange('company_phone', e.target.value)}
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Details Tab */}
        <TabsContent value="bank">
          <Card>
            <CardHeader>
              <CardTitle>Bank Account Details</CardTitle>
              <CardDescription>Bank details shown on receipts for reference</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={settings.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    placeholder="HDFC Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Input
                    value={settings.bank_branch}
                    onChange={(e) => handleChange('bank_branch', e.target.value)}
                    placeholder="MG Road Branch"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={settings.bank_account_number}
                    onChange={(e) => handleChange('bank_account_number', e.target.value)}
                    placeholder="50100123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input
                    value={settings.bank_ifsc}
                    onChange={(e) => handleChange('bank_ifsc', e.target.value.toUpperCase())}
                    placeholder="HDFC0001234"
                    maxLength={11}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 mt-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>UPI ID (for QR code on invoices)</Label>
                  <Input
                    value={settings.upi_id}
                    onChange={(e) => handleChange('upi_id', e.target.value)}
                    placeholder="yourname@bankname"
                    data-testid="upi-id-input"
                  />
                  <p className="text-xs text-gray-500">QR code will be automatically added to invoices for instant UPI payments</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GST Settings Tab */}
        <TabsContent value="gst">
          <Card>
            <CardHeader>
              <CardTitle>GST Configuration</CardTitle>
              <CardDescription>Configure GST rates for tax calculation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 text-blue-800 mb-2">
                  <i className="fas fa-info-circle"></i>
                  <span className="font-medium">GST Structure (India)</span>
                </div>
                <p className="text-sm text-blue-700">
                  Total GST is split equally between CGST (Central) and SGST (State).
                  Standard rate is 18% = CGST 9% + SGST 9%
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Total GST Rate (%)</Label>
                  <Input
                    type="number"
                    value={settings.gst_rate}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value) || 0;
                      handleChange('gst_rate', rate);
                      handleChange('cgst_rate', rate / 2);
                      handleChange('sgst_rate', rate / 2);
                    }}
                    step="0.1"
                    min="0"
                    max="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CGST Rate (%)</Label>
                  <Input
                    type="number"
                    value={settings.cgst_rate}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SGST Rate (%)</Label>
                  <Input
                    type="number"
                    value={settings.sgst_rate}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-2 text-amber-800 mb-2">
                  <i className="fas fa-calculator"></i>
                  <span className="font-medium">Example Calculation</span>
                </div>
                <p className="text-sm text-amber-700">
                  For ₹1,00,000 (GST Inclusive):<br />
                  • Base Amount: ₹{(100000 / (1 + settings.gst_rate / 100)).toFixed(2)}<br />
                  • CGST ({settings.cgst_rate}%): ₹{((100000 / (1 + settings.gst_rate / 100)) * settings.cgst_rate / 100).toFixed(2)}<br />
                  • SGST ({settings.sgst_rate}%): ₹{((100000 / (1 + settings.gst_rate / 100)) * settings.sgst_rate / 100).toFixed(2)}<br />
                  • Total: ₹1,00,000
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Document Numbering Tab */}
        <TabsContent value="numbering">
          <Card>
            <CardHeader>
              <CardTitle>Document Numbering</CardTitle>
              <CardDescription>Configure prefixes for tax invoices, proforma invoices, receipts, and credit notes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tax Invoice Prefix</Label>
                  <Input
                    value={settings.invoice_prefix}
                    onChange={(e) => handleChange('invoice_prefix', e.target.value.toUpperCase())}
                    placeholder="INV"
                    maxLength={5}
                  />
                  <p className="text-xs text-gray-500">Example: {settings.invoice_prefix}-2025-0001</p>
                </div>
                <div className="space-y-2">
                  <Label>Proforma Invoice Prefix</Label>
                  <Input
                    value={settings.proforma_prefix}
                    onChange={(e) => handleChange('proforma_prefix', e.target.value.toUpperCase())}
                    placeholder="PI"
                    maxLength={5}
                    data-testid="proforma-prefix-input"
                  />
                  <p className="text-xs text-gray-500">Pre-payment series (does not touch the Tax Invoice series). Example: {settings.proforma_prefix}-2025-0001</p>
                </div>
                <div className="space-y-2">
                  <Label>Receipt Prefix</Label>
                  <Input
                    value={settings.receipt_prefix}
                    onChange={(e) => handleChange('receipt_prefix', e.target.value.toUpperCase())}
                    placeholder="RCP"
                    maxLength={5}
                  />
                  <p className="text-xs text-gray-500">Example: {settings.receipt_prefix}-2025-0001</p>
                </div>
                <div className="space-y-2">
                  <Label>Credit Note Prefix</Label>
                  <Input
                    value={settings.credit_note_prefix}
                    onChange={(e) => handleChange('credit_note_prefix', e.target.value.toUpperCase())}
                    placeholder="CN"
                    maxLength={5}
                  />
                  <p className="text-xs text-gray-500">Example: {settings.credit_note_prefix}-2025-0001</p>
                </div>
                <div className="space-y-2">
                  <Label>Default Registration Fee (₹)</Label>
                  <Input
                    type="number"
                    value={settings.registration_fee_default}
                    onChange={(e) => handleChange('registration_fee_default', parseFloat(e.target.value) || 0)}
                    step="100"
                    min="0"
                    data-testid="registration-fee-default-input"
                  />
                  <p className="text-xs text-gray-500">Pre-filled on the Proforma Invoice. Editable per student.</p>
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <i className="fas fa-info-circle"></i>
                  <span className="font-medium">Current Counters</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Tax Invoices:</span>
                    <span className="ml-2 font-medium">{settings.current_invoice_number || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Proformas:</span>
                    <span className="ml-2 font-medium">{settings.current_proforma_number || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Receipts:</span>
                    <span className="ml-2 font-medium">{settings.current_receipt_number || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Credit Notes:</span>
                    <span className="ml-2 font-medium">{settings.current_credit_note_number || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Payment Reminders Tab (Admin Only) ──────────────────────── */}
        <TabsContent value="reminders">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fab fa-whatsapp text-emerald-600" />
                  WhatsApp Payment Reminders
                </CardTitle>
                <CardDescription>
                  Auto + manual reminders for unpaid invoices. Each message includes a Razorpay payment link for the EXACT outstanding balance.
                  {!reminder.can_edit && (
                    <span className="block mt-1 text-amber-600">View only — only admins can change these settings.</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Auto Reminders</p>
                    <p className="text-xs text-gray-600">Daily cron at 09:30 IST sweeps overdue invoices</p>
                  </div>
                  <Switch
                    checked={!!reminder.enabled}
                    onCheckedChange={(v) => setReminder(prev => ({ ...prev, enabled: v }))}
                    disabled={!reminder.can_edit}
                    data-testid="reminder-toggle"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Reminder Period (days)</Label>
                    <p className="text-[11px] text-gray-500 mb-1">How old must an unpaid invoice be before the cron sends a reminder</p>
                    <Input
                      type="number" min={1} max={365}
                      value={reminder.reminder_period_days}
                      disabled={!reminder.can_edit}
                      onChange={(e) => setReminder(prev => ({ ...prev, reminder_period_days: e.target.value }))}
                      data-testid="reminder-period-input"
                    />
                  </div>
                  <div>
                    <Label>Throttle Window (hours)</Label>
                    <p className="text-[11px] text-gray-500 mb-1">Don't re-send to the same invoice within this window</p>
                    <Input
                      type="number" min={1} max={720}
                      value={reminder.min_gap_hours}
                      disabled={!reminder.can_edit}
                      onChange={(e) => setReminder(prev => ({ ...prev, min_gap_hours: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label>WATI Template Name</Label>
                  <Input
                    value={reminder.template_name || 'payment_reminder'}
                    disabled={!reminder.can_edit}
                    onChange={(e) => setReminder(prev => ({ ...prev, template_name: e.target.value }))}
                  />
                </div>

                {reminder.can_edit && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button onClick={saveReminder} disabled={savingReminder} className="bg-emerald-600 hover:bg-emerald-700">
                      {savingReminder ? <i className="fas fa-spinner fa-spin mr-2" /> : <i className="fas fa-save mr-2" />}
                      Save Reminder Settings
                    </Button>
                    <Button onClick={runSweepNow} variant="outline" className="text-emerald-700 border-emerald-300">
                      <i className="fas fa-bolt mr-2" />Run Sweep Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* WATI Template details — exact spec for the user to register */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">WATI Template Specification</CardTitle>
                <CardDescription>
                  Create this template in your WATI dashboard, get it approved by Meta, then it's ready to use.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                  <p><b>Template Name:</b> <code className="bg-white px-1.5 py-0.5 rounded">payment_reminder</code></p>
                  <p><b>Category:</b> Utility</p>
                  <p><b>Language:</b> English</p>
                  <p><b>Header:</b> <i>none</i></p>
                  <p><b>Footer:</b> <i>(optional)</i> Patrick's Edu Care Pvt. Ltd.</p>
                </div>

                <div>
                  <p className="font-medium mb-1">Body (copy-paste into WATI):</p>
                  <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-[12px] whitespace-pre-wrap leading-relaxed">{`Hi {{1}}, gentle reminder for invoice {{2}}.

Outstanding balance: ₹{{3}}

Pay securely here: {{4}}

Thank you,
Patrick's Edu Care`}</pre>
                </div>

                <div className="overflow-x-auto">
                  <p className="font-medium mb-1">Body Variables (in this exact order):</p>
                  <table className="w-full text-xs border">
                    <thead className="bg-slate-100">
                      <tr><th className="text-left p-2 border">#</th><th className="text-left p-2 border">Name</th><th className="text-left p-2 border">Sample</th><th className="text-left p-2 border">Source</th></tr>
                    </thead>
                    <tbody>
                      <tr><td className="p-2 border">1</td><td className="p-2 border"><code>name</code></td><td className="p-2 border">Amarjith</td><td className="p-2 border">Student first name</td></tr>
                      <tr><td className="p-2 border">2</td><td className="p-2 border"><code>invoice_number</code></td><td className="p-2 border">INV-2026-0042</td><td className="p-2 border">Auto-generated invoice number</td></tr>
                      <tr><td className="p-2 border">3</td><td className="p-2 border"><code>balance</code></td><td className="p-2 border">58,000</td><td className="p-2 border">Outstanding balance (INR, no symbol — symbol baked into template)</td></tr>
                      <tr><td className="p-2 border">4</td><td className="p-2 border"><code>payment_link</code></td><td className="p-2 border">https://rzp.io/i/abc123</td><td className="p-2 border">Razorpay short URL · auto-generated for the exact balance</td></tr>
                    </tbody>
                  </table>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                  <p className="font-medium mb-1">Setup checklist before going live:</p>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li>Razorpay credentials in Settings → Integrations</li>
                    <li>WATI credentials in Settings → Integrations</li>
                    <li>Create the template above in your WATI dashboard</li>
                    <li>Wait for Meta to approve it (typically &lt; 1 hour for utility templates)</li>
                    <li>Toggle "Auto Reminders" ON above to enable the daily cron</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Save Button - At bottom */}
      <div className={embedded ? "pt-4" : "sticky bottom-4 pt-4"}>
        <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700 h-10 text-sm w-full">
          {saving ? (
            <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
          ) : (
            <><i className="fas fa-save mr-2"></i>Save Settings</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default FinanceSettings;
